import React, { createContext, useCallback, useContext, useState } from 'react';

// ---------------------------------------------------------------------------
// Supported display currencies — single source of truth.
// Rates are approximate and used only for display conversion; orders are
// always recorded in the product's native currency on the backend.
// ---------------------------------------------------------------------------

export type CurrencyCode = 'USD' | 'EUR' | 'CNY' | 'CFA' | 'TND';

export type CurrencyMeta = {
  code: CurrencyCode;
  label: string;
  symbol: string;
  flag: string;
  /** How many units of this currency equal 1 USD. */
  rateToUSD: number;
};

export const CURRENCIES: CurrencyMeta[] = [
  { code: 'USD', label: 'US Dollar',    symbol: '$',   flag: '🇺🇸', rateToUSD: 1      },
  { code: 'EUR', label: 'Euro',         symbol: '€',   flag: '🇪🇺', rateToUSD: 0.92   },
  { code: 'CNY', label: 'Chinese Yuan', symbol: '¥',   flag: '🇨🇳', rateToUSD: 7.25   },
  { code: 'CFA', label: 'Franc CFA',    symbol: 'CFA', flag: '🌍',  rateToUSD: 606    },
  { code: 'TND', label: 'Dinar TND',    symbol: 'DT',  flag: '🇹🇳', rateToUSD: 3.12   },
];

export function getCurrencyMeta(code: string): CurrencyMeta {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0]!;
}

/** Convert `amount` from `fromCode` → `toCode` using USD as the pivot. */
export function convertAmount(amount: number, fromCode: string, toCode: string): number {
  if (fromCode === toCode) return amount;
  const from = getCurrencyMeta(fromCode);
  const to   = getCurrencyMeta(toCode);
  return (amount / from.rateToUSD) * to.rateToUSD;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  /** Convert `amount` (stored in `fromCurrency`) to the active display currency as a number. */
  convert: (amount: number, fromCurrency: string) => number;
  /** Convert and format `amount` (stored in `fromCurrency`) for display. */
  format: (amount: number, fromCurrency: string) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const STORAGE_KEY = 'nomadmee_currency';

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return (CURRENCIES.find((c) => c.code === saved)?.code) ?? 'USD';
  });

  const setCurrency = useCallback((code: CurrencyCode) => {
    localStorage.setItem(STORAGE_KEY, code);
    setCurrencyState(code);
  }, []);

  const convert = useCallback(
    (amount: number, fromCurrency: string): number => convertAmount(amount, fromCurrency, currency),
    [currency],
  );

  const format = useCallback(
    (amount: number, fromCurrency: string): string => {
      const converted = convertAmount(amount, fromCurrency, currency);
      const meta = getCurrencyMeta(currency);
      const rounded = Math.round(converted);
      return `${rounded.toLocaleString()} ${meta.symbol}`;
    },
    [currency],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, format }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}
